import React, { type ReactNode, type HTMLAttributes } from 'react';
import styled from 'styled-components';
import Icon from 'components/Icon';
import FlexRow from 'components/Layout/components/Flex/components/FlexRow';

export type RatingStarsProps = HTMLAttributes<HTMLDivElement> & {
    rating: number;
    size?: 'S' | 'M' | 'L';
    outline?: boolean;
    starCount?: number;
};

function getSizeStyleProps(size: RatingStarsProps['size'] = 'M') {
    const sizeMap: Record<NonNullable<RatingStarsProps['size']>, number> = {
        S: 12,
        M: 16,
        L: 24
    };

    return {
        width: sizeMap[size],
        height: sizeMap[size]
    };
}

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;
`;

const BaseContainer = styled(FlexRow).attrs(() => ({ gap: '0px' }))``;

const OverlayContainer = styled(BaseContainer)<{ $ratingPercent: number }>`
    position: absolute;
    clip-path: rect(0 ${({ $ratingPercent }) => $ratingPercent}% 100% 0);
`;

const StarFill = styled(Icon)`
    fill: ${({ theme }) => theme.ComponentsRatingActive};
`;

const StarBackground = styled(Icon)`
    fill: ${({ theme }) => theme.ComponentsRatingInactive};
`;

function starsRenderer(componentRenderer: (i: number) => ReactNode, stars: number) {
    return Array.from(Array(stars)).map((x, i) => componentRenderer(i));
}

export default function RatingStars(props: RatingStarsProps): JSX.Element {
    const { rating, size, starCount = 5, ...restProps } = props;

    /*
    Rating value from backend is a float between 0 and 5.
    Therefore it needs adjusting if we want to display fewer or more stars.
    Alternatively backend should send a percent value instead.
    */
    const ratingAdjust = starCount / 5;
    const percent = ((rating * ratingAdjust) / starCount) * 100;

    const starSizeProps = getSizeStyleProps(size);

    return (
        <Wrapper {...restProps}>
            <OverlayContainer $ratingPercent={percent}>
                {starsRenderer(
                    (i) => (
                        <StarFill key={i} type="StarFilled" size={16} {...starSizeProps} />
                    ),
                    starCount
                )}
            </OverlayContainer>
            <BaseContainer>
                {starsRenderer(
                    (i) => (
                        <StarBackground key={i} type="StarFilled" size={16} {...starSizeProps} />
                    ),
                    starCount
                )}
            </BaseContainer>
        </Wrapper>
    );
}
